/**
 * CreditNoteDetailPage - Credit Note detail view
 * EF-CREDIT-NOTE-UI-V1: Frontend UI for Credit Notes
 * 
 * Features:
 * - View credit note details (status, number, dates)
 * - Link to source invoice
 * - Editable line items (draft only)
 * - Issue credit note action
 * - PDF download
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getCreditNote,
  getInvoice,
  getClient,
  getPlatformSettings,
  updateCreditNoteLine,
  issueCreditNote,
  downloadCreditNotePdf,
  extractErrorMessage,
} from "../lib/api";
import { useFormatters } from "../components/shared/useFormatters";
import { LoadingSpinner } from "../components/shared/DataComponents";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  DocumentHeader,
  DocumentFooter,
  printStyles,
} from "../components/financial";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Download, 
  FileText, 
  Lock, 
  Save,
  ExternalLink,
  AlertCircle,
  CheckCircle2
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function CreditNoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatDate, formatCurrency, currencySymbol: platformCurrencySymbol } = useFormatters();
  
  const [creditNote, setCreditNote] = useState(null);
  const [sourceInvoice, setSourceInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [confirmIssueOpen, setConfirmIssueOpen] = useState(false);
  
  // Edit state for line items
  const [editedLines, setEditedLines] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Use document-level currency symbol if available
  const currencySymbol = creditNote?.currency_symbol || platformCurrencySymbol;
  
  // Check if credit note is editable (draft only)
  const isEditable = creditNote?.status === "draft" && !creditNote?.locked;
  const isIssued = creditNote?.status === "issued";

  const loadCreditNote = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // Load credit note
      const cnRes = await getCreditNote(id);
      const cnData = cnRes.data;
      setCreditNote(cnData);

      // Load related data in parallel
      const promises = [];
      
      if (cnData.source_invoice_id) {
        promises.push(
          getInvoice(cnData.source_invoice_id)
            .then((res) => setSourceInvoice(res.data))
            .catch(() => null)
        );
      }
      
      if (cnData.client_id) {
        promises.push(
          getClient(cnData.client_id)
            .then((res) => setClient(res.data))
            .catch(() => null)
        );
      }
      
      promises.push(
        getPlatformSettings()
          .then((res) => setCompanyProfile(res.data))
          .catch(() => null)
      );
      
      await Promise.all(promises);
    } catch (err) {
      console.error("Failed to load credit note:", extractErrorMessage(err));
      toast.error("Failed to load credit note");
      navigate("/invoices");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadCreditNote();
  }, [loadCreditNote]);

  // Handle line item edit
  const handleLineEdit = (lineId, field, value) => {
    setEditedLines(prev => ({
      ...prev,
      [lineId]: {
        ...(prev[lineId] || {}),
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Save line item changes
  const handleSaveLineChanges = async () => {
    if (!hasUnsavedChanges) return;
    
    setSaving(true);
    try {
      // Save each edited line
      for (const [lineId, changes] of Object.entries(editedLines)) {
        if (Object.keys(changes).length > 0) {
          await updateCreditNoteLine(id, lineId, changes);
        }
      }
      
      // Reload credit note to get updated totals
      await loadCreditNote();
      setEditedLines({});
      setHasUnsavedChanges(false);
      toast.success("Changes saved");
    } catch (err) {
      console.error("Failed to save changes:", extractErrorMessage(err));
      toast.error("Failed to save changes: " + extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Issue credit note
  const handleIssueCreditNote = async () => {
    setIssuing(true);
    try {
      const res = await issueCreditNote(id);
      setCreditNote(res.data);
      setConfirmIssueOpen(false);
      toast.success(`Credit Note ${res.data.credit_note_number} issued successfully`);
    } catch (err) {
      console.error("Failed to issue credit note:", extractErrorMessage(err));
      toast.error("Failed to issue: " + extractErrorMessage(err));
    } finally {
      setIssuing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!creditNote) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p>Credit Note not found</p>
        </div>
      </div>
    );
  }

  // Get line values (edited or original)
  const getLineValue = (line, field) => {
    if (editedLines[line.id] && editedLines[line.id][field] !== undefined) {
      return editedLines[line.id][field];
    }
    return line[field];
  };

  // Calculate totals from lines (use snapshot values)
  const subtotalHT = creditNote.subtotal_ht_snapshot || 0;
  const totalVAT = creditNote.total_vat_snapshot || 0;
  const totalTTC = creditNote.total_ttc_snapshot || 0;

  // Status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Draft</Badge>;
      case "issued":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Issued</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div 
      className="min-h-screen bg-surface-base py-8 px-4 print:bg-white print:p-0"
      data-testid="credit-note-detail-page"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Actions Bar */}
        <div className="flex items-center justify-between gap-4 print:hidden">
          <Button
            variant="ghost"
            onClick={() => navigate(`/invoices/${creditNote.source_invoice_id}`)}
            className="gap-2"
            data-testid="back-to-invoice-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Invoice
          </Button>
          
          <div className="flex items-center gap-2">
            {isEditable && hasUnsavedChanges && (
              <Button
                variant="outline"
                onClick={handleSaveLineChanges}
                disabled={saving}
                className="gap-2"
                data-testid="save-changes-btn"
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            )}
            
            {isEditable && (
              <Button
                onClick={() => setConfirmIssueOpen(true)}
                disabled={hasUnsavedChanges || issuing}
                className="gap-2 bg-teal-600 hover:bg-teal-700"
                data-testid="issue-credit-note-btn"
              >
                <CheckCircle2 className="h-4 w-4" />
                Issue Credit Note
              </Button>
            )}
            
            {isIssued && (
              <Button
                variant="outline"
                onClick={() =>
                  downloadCreditNotePdf(id, creditNote.credit_note_number).catch(() =>
                    toast.error("Failed to download PDF")
                  )
                }
                className="gap-2"
                data-testid="download-pdf-btn"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            )}
          </div>
        </div>

        {/* Credit Note Card */}
        <Card className="overflow-hidden bg-white print:shadow-none print:rounded-none">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold mb-1">CREDIT NOTE / AVOIR</h1>
                {creditNote.credit_note_number ? (
                  <p className="text-red-100 text-lg font-mono" data-testid="credit-note-number">
                    {creditNote.credit_note_number}
                  </p>
                ) : (
                  <p className="text-red-200 italic">Draft - No number assigned</p>
                )}
              </div>
              <div className="text-right">
                {getStatusBadge(creditNote.status)}
                {creditNote.locked && (
                  <div className="flex items-center gap-1 mt-2 text-red-200 text-sm">
                    <Lock className="h-3 w-3" />
                    Locked
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="p-6 border-b border-border">
            <div className="grid grid-cols-2 gap-6">
              {/* Left - Document Info */}
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-muted-foreground">Issue Date</span>
                  <p className="font-medium" data-testid="issue-date">
                    {formatDate(creditNote.issue_date)}
                  </p>
                </div>
                {creditNote.issued_at && (
                  <div>
                    <span className="text-sm text-muted-foreground">Issued At</span>
                    <p className="font-medium" data-testid="issued-at">
                      {formatDate(creditNote.issued_at)}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-sm text-muted-foreground">Source Invoice</span>
                  <p className="font-medium">
                    <Link 
                      to={`/invoices/${creditNote.source_invoice_id}`}
                      className="text-primary hover:underline flex items-center gap-1"
                      data-testid="source-invoice-link"
                    >
                      <FileText className="h-4 w-4" />
                      {creditNote.source_invoice_number || "View Invoice"}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </p>
                </div>
              </div>
              
              {/* Right - Client Info */}
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-muted-foreground">Client</span>
                  <p className="font-medium" data-testid="client-name">
                    {creditNote.client_name_snapshot || client?.name || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Project</span>
                  <p className="font-medium" data-testid="project-name">
                    {creditNote.project_name_snapshot || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="p-6">
            <h3 className="font-semibold mb-4 text-lg">Credit Items</h3>
            
            {!isEditable && (
              <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                <Lock className="h-4 w-4" />
                This credit note is locked and cannot be edited.
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="credit-note-lines-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Description</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground w-24">Qty</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground w-28">Unit Rate</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground w-24">VAT %</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground w-28">Net HT</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground w-28">VAT</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground w-28">Total TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {creditNote.lines?.map((line, idx) => (
                    <tr key={line.id} className="border-b border-border/50" data-testid={`credit-line-${idx}`}>
                      <td className="py-3 px-2">
                        {line.name_snapshot}
                      </td>
                      <td className="py-3 px-2 text-right">
                        {isEditable ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={getLineValue(line, 'quantity')}
                            onChange={(e) => handleLineEdit(line.id, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-20 text-right h-8"
                            data-testid={`line-qty-${idx}`}
                          />
                        ) : (
                          <span className="font-mono">{line.quantity}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        {isEditable ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={getLineValue(line, 'unit_rate_snapshot')}
                            onChange={(e) => handleLineEdit(line.id, 'unit_rate_snapshot', parseFloat(e.target.value) || 0)}
                            className="w-24 text-right h-8"
                            data-testid={`line-rate-${idx}`}
                          />
                        ) : (
                          <span className="font-mono">{currencySymbol}{Math.abs(line.unit_rate_snapshot || 0).toFixed(2)}</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right font-mono">
                        {((line.tax_rate_snapshot || 0) * 100).toFixed(0)}%
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-red-600">
                        {currencySymbol}{(line.net_ht_snapshot || 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-red-600">
                        {currencySymbol}{(line.tax_amount_snapshot || 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-right font-mono font-semibold text-red-600">
                        {currencySymbol}{(line.total_ttc_snapshot || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section */}
          <div className="p-6 bg-muted/30">
            <div className="flex justify-end">
              <div className="w-full max-w-sm space-y-2" data-testid="credit-note-totals">
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-muted-foreground">Subtotal HT</span>
                  <span className="font-mono font-medium text-red-600" data-testid="subtotal-ht">
                    {currencySymbol}{subtotalHT.toFixed(2)}
                  </span>
                </div>
                
                {/* VAT Summary */}
                {creditNote.vat_summary_snapshot && Object.entries(creditNote.vat_summary_snapshot).map(([rate, amount]) => (
                  <div key={rate} className="flex justify-between py-2 text-sm">
                    <span className="text-muted-foreground">
                      VAT {(parseFloat(rate) * 100).toFixed(0)}%
                    </span>
                    <span className="font-mono font-medium text-red-600">
                      {currencySymbol}{amount.toFixed(2)}
                    </span>
                  </div>
                ))}
                
                <div className="flex justify-between py-2 text-sm border-t border-border">
                  <span className="text-muted-foreground">Total VAT</span>
                  <span className="font-mono font-medium text-red-600" data-testid="total-vat">
                    {currencySymbol}{totalVAT.toFixed(2)}
                  </span>
                </div>
                
                <div className="flex justify-between py-3 text-lg font-bold border-t border-border">
                  <span>Total Credit TTC</span>
                  <span className="font-mono text-red-600" data-testid="total-ttc">
                    {currencySymbol}{totalTTC.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes/Reason Section */}
          {(creditNote.reason || creditNote.notes) && (
            <div className="p-6 border-t border-border">
              {creditNote.reason && (
                <div className="mb-4">
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Reason</h4>
                  <p>{creditNote.reason}</p>
                </div>
              )}
              {creditNote.notes && (
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Notes</h4>
                  <p>{creditNote.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <DocumentFooter
            footerText={companyProfile?.credit_note_footer || companyProfile?.invoice_footer}
            formatDate={formatDate}
          />
        </Card>

        {/* Warning for draft */}
        {isEditable && (
          <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 print:hidden">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">This credit note is in draft status</p>
              <p className="text-sm mt-1">
                Click "Issue Credit Note" to finalize it. Once issued, the credit note will be locked 
                and assigned a number. This action cannot be undone.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{printStyles}</style>

      {/* Confirm Issue Dialog */}
      <AlertDialog open={confirmIssueOpen} onOpenChange={setConfirmIssueOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Issue Credit Note?</AlertDialogTitle>
            <AlertDialogDescription>
              This will finalize the credit note and assign it a number (CRN-YYYY-NNNNN).
              Once issued, the credit note will be locked and cannot be modified.
              <br /><br />
              <strong>Total Credit: {currencySymbol}{totalTTC.toFixed(2)}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={issuing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleIssueCreditNote}
              disabled={issuing}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {issuing ? "Issuing..." : "Issue Credit Note"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
